import { decodeCurrency, normalizeCurrency, transactionUrl } from './format.js';
import { decodeAccountControls, decodeMptControls } from './controls.js';

export const XRPL_ENDPOINTS = [
  'wss://s2.ripple.com',
  'wss://xrplcluster.com',
  'wss://xrpl.ws',
];

const REQUEST_TIMEOUT = 15_000;

export class XrplSocket {
  constructor({ onStream, onClose, onError } = {}) {
    this.socket = null;
    this.endpoint = null;
    this.nextId = 1;
    this.pending = new Map();
    this.onStream = onStream;
    this.onClose = onClose;
    this.onError = onError;
    this.intentionalClose = false;
    this.probing = false;
  }

  async connect() {
    let lastError;
    this.probing = true;
    for (const endpoint of XRPL_ENDPOINTS) {
      try {
        await this.open(endpoint);
        await this.request('server_info');
        this.endpoint = endpoint;
        this.probing = false;
        return endpoint;
      } catch (error) {
        lastError = error;
        this.socket?.close();
        this.socket = null;
      }
    }
    this.probing = false;
    throw lastError || new Error('No XRPL WebSocket endpoint was reachable.');
  }

  open(endpoint) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(endpoint);
      const timeout = setTimeout(() => {
        socket.close();
        reject(new Error(`Timed out connecting to ${endpoint}`));
      }, REQUEST_TIMEOUT);

      socket.addEventListener('open', () => {
        clearTimeout(timeout);
        this.socket = socket;
        socket.addEventListener('message', (event) => this.handleMessage(event));
        socket.addEventListener('close', () => this.handleClose());
        socket.addEventListener('error', (error) => this.onError?.(error));
        resolve();
      }, { once: true });

      socket.addEventListener('error', () => {
        clearTimeout(timeout);
        reject(new Error(`Could not connect to ${endpoint}`));
      }, { once: true });
    });
  }

  handleMessage(event) {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    if (message.id !== undefined && this.pending.has(message.id)) {
      const { resolve, reject, timeout } = this.pending.get(message.id);
      clearTimeout(timeout);
      this.pending.delete(message.id);
      if (message.status === 'error' || message.error) {
        reject(new Error(message.error_message || message.error || 'XRPL request failed'));
      } else {
        resolve(message.result ?? message);
      }
      return;
    }

    this.onStream?.(message);
  }

  handleClose() {
    for (const { reject, timeout } of this.pending.values()) {
      clearTimeout(timeout);
      reject(new Error('XRPL connection closed before the request completed.'));
    }
    this.pending.clear();
    this.socket = null;
    if (!this.intentionalClose && !this.probing) this.onClose?.();
  }

  request(command, parameters = {}) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('XRPL WebSocket is not connected.'));
    }

    const id = this.nextId++;
    const payload = { id, command, ...parameters };
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`${command} request timed out.`));
      }, REQUEST_TIMEOUT);
      this.pending.set(id, { resolve, reject, timeout });
      this.socket.send(JSON.stringify(payload));
    });
  }

  close() {
    this.intentionalClose = true;
    this.socket?.close();
  }
}

export function instrumentKey(programId, instrument, currency = instrument.currency) {
  const identity = instrument.kind === 'mpt'
    ? instrument.issuanceId
    : `${instrument.issuer}:${normalizeCurrency(currency)}`;
  return `${programId}:${identity}`;
}

export function buildMatcher(programs) {
  const classicIndex = new Map();
  const wildcardIndex = new Map();
  const mptIndex = new Map();
  const accountIndex = new Map();

  for (const program of programs) {
    for (const instrument of program.instruments || []) {
      if (!accountIndex.has(instrument.issuer)) accountIndex.set(instrument.issuer, []);
      accountIndex.get(instrument.issuer).push({ program, instrument });

      if (instrument.kind === 'mpt') {
        mptIndex.set(instrument.issuanceId.toUpperCase(), { program, instrument });
      } else if (instrument.kind === 'issuer-series') {
        if (!wildcardIndex.has(instrument.issuer)) wildcardIndex.set(instrument.issuer, []);
        wildcardIndex.get(instrument.issuer).push({ program, instrument });
      } else {
        const key = `${instrument.issuer}:${normalizeCurrency(instrument.currency)}`;
        if (!classicIndex.has(key)) classicIndex.set(key, []);
        classicIndex.get(key).push({ program, instrument });
      }
    }
  }

  const matchIssue = (issuer, currency) => {
    if (!issuer || !currency) return [];
    const normalized = normalizeCurrency(currency);
    const exact = classicIndex.get(`${issuer}:${normalized}`) || [];
    const wildcard = (wildcardIndex.get(issuer) || []).map(({ program, instrument }) => ({
      program,
      instrument: {
        ...instrument,
        currency,
        ticker: decodeCurrency(currency),
        derivedSeries: true,
      },
    }));
    return [...exact, ...wildcard];
  };

  const matchAmount = (amount) => {
    if (!amount || typeof amount !== 'object') return [];
    if (amount.mpt_issuance_id) {
      const match = mptIndex.get(String(amount.mpt_issuance_id).toUpperCase());
      return match ? [match] : [];
    }
    return matchIssue(amount.issuer, amount.currency);
  };

  return { classicIndex, wildcardIndex, mptIndex, accountIndex, matchIssue, matchAmount };
}

function transactionParts(event) {
  const wrapper = event?.tx_json || event?.transaction || event?.tx || event || {};
  const tx = wrapper.tx_json || wrapper.transaction || wrapper.tx || wrapper;
  const meta = event?.meta || event?.metadata || wrapper?.meta || {};
  const validated = event?.validated ?? wrapper?.validated ?? true;
  return { tx, meta, validated };
}

function amountValue(amount, instrument) {
  if (!amount || typeof amount !== 'object') return null;
  const raw = Number(amount.value);
  if (!Number.isFinite(raw)) return null;
  const scale = Number(instrument?.assetScale || 0);
  return instrument?.kind === 'mpt' && scale > 0 ? raw / (10 ** scale) : raw;
}

function amountLabel(amount) {
  if (amount === undefined || amount === null) return null;
  if (typeof amount === 'string') return { value: Number(amount) / 1_000_000, ticker: 'XRP', native: true };
  if (typeof amount !== 'object') return null;
  return {
    value: Number(amount.value),
    ticker: amount.mpt_issuance_id ? 'MPT' : decodeCurrency(amount.currency),
    issuer: amount.issuer,
    issuanceId: amount.mpt_issuance_id,
  };
}

function collectTransactionMatches(tx, meta, matcher) {
  const amounts = [
    meta?.delivered_amount,
    meta?.DeliveredAmount,
    tx.Amount,
    tx.DeliverMax,
    tx.SendMax,
    tx.TakerGets,
    tx.TakerPays,
    tx.LimitAmount,
  ].filter(Boolean);
  const matches = [];
  for (const amount of amounts) {
    for (const match of matcher.matchAmount(amount)) {
      matches.push({ ...match, amount });
    }
  }

  const possibleMptIds = [
    tx.MPTokenIssuanceID,
    tx.MPTokenIssuanceId,
    tx.MPTokenIssuance,
  ].filter(Boolean);
  for (const id of possibleMptIds) {
    const match = matcher.mptIndex.get(String(id).toUpperCase());
    if (match) matches.push({ ...match, amount: { mpt_issuance_id: id, value: tx.Amount?.value } });
  }

  const unique = new Map();
  for (const match of matches) {
    const key = instrumentKey(match.program.id, match.instrument, match.instrument.currency);
    if (!unique.has(key)) unique.set(key, match);
  }
  return [...unique.values()];
}

function rippleStateSettlements(meta, matcher) {
  const totals = new Map();
  for (const affected of meta?.AffectedNodes || []) {
    const node = affected.ModifiedNode || affected.CreatedNode || affected.DeletedNode;
    if (!node || node.LedgerEntryType !== 'RippleState') continue;
    const finalFields = node.FinalFields || node.NewFields || {};
    const previousFields = node.PreviousFields || {};
    const balance = finalFields.Balance;
    const previous = previousFields.Balance;
    if (!balance?.currency) continue;

    const low = finalFields.LowLimit?.issuer;
    const high = finalFields.HighLimit?.issuer;
    const candidates = [low, high].flatMap((issuer) => matcher.matchIssue(issuer, balance.currency));
    if (!candidates.length) continue;

    const delta = Number(balance.value || 0) - Number(previous?.value || 0);
    if (!Number.isFinite(delta) || delta === 0) continue;
    for (const candidate of candidates) {
      const issuer = candidate.instrument.issuer;
      const holderDelta = issuer === high ? delta : -delta;
      const key = instrumentKey(candidate.program.id, candidate.instrument, balance.currency);
      const entry = totals.get(key) || { ...candidate, positive: 0, negative: 0 };
      if (holderDelta > 0) entry.positive += holderDelta;
      if (holderDelta < 0) entry.negative += Math.abs(holderDelta);
      totals.set(key, entry);
    }
  }

  return [...totals.values()].map((entry) => ({
    ...entry,
    moved: Math.max(entry.positive, entry.negative),
  }));
}

function actionForTransaction(tx, primaryMatch, hasSettlement) {
  const type = tx.TransactionType || 'Transaction';
  const issuer = primaryMatch?.instrument?.issuer;
  if (type === 'Payment') {
    if (issuer && tx.Account === issuer) return 'Issue / distribute';
    if (issuer && tx.Destination === issuer) return 'Redeem / return';
    return 'Transfer';
  }
  if (type === 'OfferCreate') return hasSettlement ? 'DEX settlement + order' : 'DEX order placed';
  if (type === 'OfferCancel') return 'DEX order cancelled';
  if (type === 'TrustSet') return 'Trust-line update';
  if (type === 'Clawback') return 'Clawback';
  if (type.startsWith('AMM')) return type.replace('AMM', 'AMM ');
  if (type.startsWith('MPTokenIssuance')) return type.replace('MPTokenIssuance', 'MPT issuance ');
  if (type.startsWith('MPToken')) return type.replace('MPToken', 'MPT ');
  return 'Issuer-account activity';
}

export function classifyFlow(event, matcher, source = 'live') {
  const { tx, meta, validated } = transactionParts(event);
  if (!validated) return null;
  const result = meta?.TransactionResult || meta?.transaction_result;
  if (result && result !== 'tesSUCCESS') return null;

  const matches = collectTransactionMatches(tx, meta, matcher);
  const settlements = rippleStateSettlements(meta, matcher);
  const accountMatches = [
    ...(matcher.accountIndex.get(tx.Account) || []),
    ...(matcher.accountIndex.get(tx.Destination) || []),
  ];
  const primary = matches[0] || settlements[0] || accountMatches[0];
  if (!primary) return null;

  const delivered = meta?.delivered_amount ?? meta?.DeliveredAmount ?? tx.Amount;
  const deliveredMatch = matcher.matchAmount(delivered)[0];
  const settlement = settlements.find(({ program }) => program.id === primary.program.id);
  let displayAmount = null;
  let amountTicker = primary.instrument.ticker || primary.program.ticker;
  let amountRole = null;

  if (tx.TransactionType === 'Payment' && deliveredMatch) {
    displayAmount = amountValue(delivered, deliveredMatch.instrument);
    amountTicker = deliveredMatch.instrument.ticker;
    amountRole = 'delivered';
  } else if (tx.TransactionType === 'Payment' && settlement?.moved) {
    displayAmount = settlement.moved;
    amountTicker = settlement.instrument.ticker;
    amountRole = 'settled asset delta';
  } else if (tx.TransactionType === 'OfferCreate' && settlement?.moved) {
    displayAmount = settlement.moved;
    amountTicker = settlement.instrument.ticker;
    amountRole = 'settled';
  } else if (tx.TransactionType === 'TrustSet' && primary.amount) {
    displayAmount = amountValue(primary.amount, primary.instrument);
    amountRole = 'trust limit';
  } else if (primary.amount && ['Clawback', 'AMMDeposit', 'AMMWithdraw'].includes(tx.TransactionType)) {
    displayAmount = amountValue(primary.amount, primary.instrument);
  }

  const pair = tx.TransactionType === 'OfferCreate'
    ? [amountLabel(tx.TakerGets), amountLabel(tx.TakerPays)].filter(Boolean)
    : null;
  const hash = tx.hash || tx.Hash || event?.hash;
  const ledgerIndex = tx.ledger_index || tx.inLedger || event?.ledger_index || event?.ledger_index_max;
  const allMatches = [...matches, ...settlements, ...accountMatches]
    .filter((match, index, items) => items.findIndex((item) => item.program.id === match.program.id) === index)
    .map(({ program, instrument }) => ({
      programId: program.id,
      name: program.name,
      ticker: instrument.ticker || program.ticker,
    }));

  let action = actionForTransaction(tx, primary, Boolean(settlement?.moved));
  if (tx.TransactionType === 'Payment' && !matches.length && !settlements.length) {
    action = 'Issuer-account payment';
  } else if (tx.TransactionType === 'Payment' && !deliveredMatch) {
    action = 'Payment path / conversion';
  }

  return {
    id: hash || `${ledgerIndex || 'pending'}:${tx.Account || 'unknown'}:${tx.Sequence || tx.TicketSequence || 0}:${tx.TransactionType || 'transaction'}`,
    hash,
    href: transactionUrl(hash),
    programId: primary.program.id,
    programName: primary.program.name,
    category: primary.program.category,
    verification: primary.program.verification,
    ticker: amountTicker,
    amount: displayAmount,
    amountRole,
    action,
    transactionType: tx.TransactionType || 'Transaction',
    account: tx.Account,
    destination: tx.Destination,
    ledgerIndex,
    date: tx.date ?? event?.close_time,
    validated: Boolean(validated),
    source,
    pair,
    matches: allMatches,
  };
}

export async function fetchLedgerSnapshot(client, programs) {
  const issuerMap = new Map();
  const mptInstruments = [];
  for (const program of programs) {
    for (const instrument of program.instruments || []) {
      if (instrument.kind === 'mpt') {
        mptInstruments.push({ program, instrument });
      } else {
        if (!issuerMap.has(instrument.issuer)) issuerMap.set(instrument.issuer, []);
        issuerMap.get(instrument.issuer).push({ program, instrument });
      }
    }
  }

  const supplies = [];
  const controlsByIssuer = new Map();
  let latestLedger = null;
  const issuerResults = await mapLimit([...issuerMap.entries()], 4, async ([issuer, entries]) => {
    const [result, info] = await Promise.all([
      client.request('gateway_balances', {
        account: issuer,
        ledger_index: 'validated',
        strict: true,
      }),
      // Issuer controls are useful but optional; a failed account_info must not drop the balance row.
      client.request('account_info', { account: issuer, ledger_index: 'validated' }).catch(() => null),
    ]);
    return { issuer, entries, result, info };
  });

  for (const issuerResult of issuerResults) {
    if (issuerResult.status === 'rejected') continue;
    const { issuer, entries, result, info } = issuerResult.value;
    latestLedger = Math.max(latestLedger || 0, Number(result.ledger_index || 0));
    const obligations = result.obligations || {};
    const controls = info?.account_data ? decodeAccountControls(info.account_data) : null;
    if (controls) controlsByIssuer.set(issuer, controls);

    for (const { program, instrument } of entries) {
      const matchingEntries = instrument.kind === 'issuer-series'
        ? Object.entries(obligations)
        : Object.entries(obligations).filter(([currency]) => normalizeCurrency(currency) === normalizeCurrency(instrument.currency));

      if (!matchingEntries.length) matchingEntries.push([instrument.currency, '0']);
      for (const [currency, value] of matchingEntries) {
        const gross = Math.max(0, Number(value) || 0);
        let excluded = 0;
        for (const holder of instrument.excludedHolders || []) {
          try {
            const holderResult = await client.request('account_lines', {
              account: holder,
              peer: issuer,
              ledger_index: 'validated',
              limit: 400,
            });
            const line = (holderResult.lines || []).find((item) => normalizeCurrency(item.currency) === normalizeCurrency(currency));
            excluded += Math.max(0, Number(line?.balance) || 0);
          } catch {
            // The gross ledger obligation remains useful if a treasury line cannot be fetched.
          }
        }
        supplies.push({
          key: instrumentKey(program.id, instrument, currency),
          programId: program.id,
          programName: program.name,
          category: program.category,
          availability: program.availability,
          verification: program.verification,
          kind: 'classic',
          issuer,
          currency,
          ticker: instrument.kind === 'issuer-series' && currency !== '*' ? decodeCurrency(currency) : instrument.ticker,
          unit: instrument.unit || decodeCurrency(currency),
          gross,
          excluded,
          circulating: Math.max(0, gross - excluded),
          ledgerIndex: result.ledger_index,
          supplyNote: instrument.supplyNote,
          excludeFromActive: Boolean(instrument.excludeFromActive),
          controls,
        });
      }
    }
  }

  const mptResults = await mapLimit(mptInstruments, 3, async ({ program, instrument }) => {
    const result = await client.request('ledger_entry', {
      mpt_issuance: instrument.issuanceId,
      ledger_index: 'validated',
    });
    return { program, instrument, result };
  });

  for (const mptResult of mptResults) {
    if (mptResult.status === 'rejected') continue;
    const { program, instrument, result } = mptResult.value;
    const node = result.node || {};
    const scale = Number(node.AssetScale ?? instrument.assetScale ?? 0);
    const divisor = 10 ** scale;
    const gross = Number(node.OutstandingAmount || 0) / divisor;
    latestLedger = Math.max(latestLedger || 0, Number(result.ledger_index || 0));
    supplies.push({
      key: instrumentKey(program.id, instrument),
      programId: program.id,
      programName: program.name,
      category: program.category,
      availability: program.availability,
      verification: program.verification,
      kind: 'mpt',
      issuer: instrument.issuer,
      issuanceId: instrument.issuanceId,
      currency: instrument.issuanceId,
      ticker: instrument.ticker,
      unit: instrument.unit || 'units',
      gross,
      excluded: 0,
      circulating: gross,
      ledgerIndex: result.ledger_index,
      assetScale: scale,
      maximum: node.MaximumAmount ? Number(node.MaximumAmount) / divisor : null,
      controls: decodeMptControls(node),
    });
  }

  return { supplies, ledgerIndex: latestLedger, controlsByIssuer };
}

export async function fetchRecentFlows(client, programs, matcher) {
  const accounts = [...new Set(programs.flatMap((program) =>
    (program.instruments || []).map((instrument) => instrument.issuer),
  ).filter(Boolean))];
  const results = await mapLimit(accounts, 3, async (account) => client.request('account_tx', {
    account,
    ledger_index_min: -1,
    ledger_index_max: -1,
    binary: false,
    forward: false,
    limit: 8,
  }));

  const flows = [];
  const seen = new Set();
  for (const result of results) {
    if (result.status === 'rejected') continue;
    for (const entry of result.value.transactions || []) {
      const flow = classifyFlow(entry, matcher, 'history');
      if (!flow || seen.has(flow.id)) continue;
      seen.add(flow.id);
      flows.push(flow);
    }
  }
  return flows.sort((a, b) => Number(b.date || 0) - Number(a.date || 0)).slice(0, 80);
}

export async function mapLimit(items, limit, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;
  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      try {
        results[index] = { status: 'fulfilled', value: await mapper(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}
